import { Icon, MenuGroupContent, MenuOption } from "@smartsuite/react-ui/lib";
import { IntegrationLabelStyled } from "../style";
import { translateIfPresent } from "../../../../../../locales/i18n";
import type { InputConfig } from "@smartsuite/automation-engine-api";
import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import type { ActionCandidate } from "../../../../../../types/candidate";
import { SolutionColorVariables } from "@smartsuite/types";

interface InputFieldGroup extends InputConfig {
  nestedInputs: InputConfig[];
  groupFieldId: string;
  label: string;
}

export const InputFieldOptions: React.FC<{
  options: InputConfig[];
  action: ActionCandidate;
  onClickMenu: (inputDesc: InputConfig) => void;
  goSubMenu?: (options: InputConfig[], label: string) => void;
}> = ({ options, action, onClickMenu, goSubMenu }) => {
  const [search, setSearch] = useState<string>("");
  const [groupedOptions, setGroupedOptions] = useState<InputFieldGroup[]>([]);

  const findLabel = (input: InputFieldGroup): string => {
    const description = action.description.inputs.find(
      (desc) => desc.inputId === input.inputId,
    );
    if (description !== undefined) {
      return description.labelSuffix && input.nestedInputs.length === 0
        ? `${description.label}: ${description.labelSuffix}`
        : description.label;
    }
    return "";
  };

  const getFieldIcon = useMemo(
    () =>
      (input: InputConfig): ReactElement => {
        const description = action.description.inputs.find(
          (item) => item.inputId === input.inputId,
        );
        return (
          <Icon
            size="S"
            name={description?.icon}
            color={SolutionColorVariables.sc1}
          />
        );
      },
    [action.description.inputs],
  );

  useEffect(() => {
    const newGroupedOptions: InputFieldGroup[] = [];
    options.forEach((input) => {
      const description = action.description.inputs.find(
        (item) => item.inputId === input.inputId,
      );
      if (description?.groupFieldId !== undefined) {
        const group = newGroupedOptions.find(
          (group) => group.groupFieldId === description.groupFieldId,
        );
        if (group === undefined) {
          newGroupedOptions.push({
            ...input,
            nestedInputs: [],
            groupFieldId: description.groupFieldId,
            label: description.label,
          });
        } else {
          group.nestedInputs.push(input);
        }
      } else {
        newGroupedOptions.push({
          ...input,
          nestedInputs: [],
          groupFieldId: "",
          label: description?.label ?? "",
        });
      }
    });

    setGroupedOptions(newGroupedOptions);
  }, [options]);

  const onOptionClick = (inputFieldGroup: InputFieldGroup): void => {
    if (inputFieldGroup.nestedInputs?.length) {
      if (goSubMenu) {
        const nestedInputs = inputFieldGroup.nestedInputs;
        goSubMenu([...nestedInputs, inputFieldGroup], inputFieldGroup.label);
      }
    } else {
      onClickMenu(inputFieldGroup);
    }
  };

  return (
    <MenuGroupContent
      search={options.length > 5}
      onSearchChange={(text) => setSearch(text)}
      maxHeight={400}
      smallListPaddings={true}
    >
      {groupedOptions
        .map((input) => ({
          label: findLabel(input),
          input,
          isGroup: input.nestedInputs.length > 0,
        }))
        .filter((option) =>
          search.length > 0
            ? option.label
                .toLocaleLowerCase()
                .includes(search.toLocaleLowerCase())
            : true,
        )
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(({ label, input, isGroup }, index) => {
          const icon = getFieldIcon(input);
          const hasIcon = !!icon.props.name;

          return (
            <MenuOption
              submenuArrow={isGroup ? "right" : undefined}
              key={`field-option-${action.hash}-${input.inputId}-${index}`}
              renderIcon={() => (hasIcon ? icon : <></>)}
              onClick={(e) => {
                e.stopPropagation();
                onOptionClick(input);
                setSearch("");
              }}
            >
              <IntegrationLabelStyled paddingLeft={!hasIcon ? "0" : undefined}>
                {translateIfPresent(`caption.${label}`, label)}
              </IntegrationLabelStyled>
            </MenuOption>
          );
        })}
    </MenuGroupContent>
  );
};
